<script setup>
import { reactive, onMounted, ref, defineProps, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useInbox } from 'dashboard/composables/useInbox';
import { CSAT_DISPLAY_TYPES } from 'shared/constants/messages';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import SectionLayout from 'dashboard/routes/dashboard/settings/account/components/SectionLayout.vue';
import CSATDisplayTypeSelector from './components/CSATDisplayTypeSelector.vue';
import CSATTemplate from 'dashboard/components-next/message/bubbles/Template/CSAT.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'next/switch/Switch.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import whatsappTemplateLanguages from './whatsappTemplateLanguages.js';
import ConfirmTemplateUpdateDialog from './components/ConfirmTemplateUpdateDialog.vue';
import ExistingTemplateSelector from './components/ExistingTemplateSelector.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const labels = useMapGetter('labels/getLabels');

const { isATwilioWhatsAppChannel, isAWhatsAppCloudChannel } = useInbox(
  props.inbox?.id
);

// WhatsApp channels that require CSAT templates (Cloud and Twilio, NOT Baileys/Z-API)
const isTemplateRequiredWhatsAppChannel = computed(
  () => isAWhatsAppCloudChannel.value || isATwilioWhatsAppChannel.value
);

const isUpdating = ref(false);
const selectedLabelValues = ref([]);
const currentLabel = ref('');

const state = reactive({
  csatSurveyEnabled: false,
  displayType: 'emoji',
  message: '',
  templateButtonText: 'Please rate us',
  surveyRuleOperator: 'contains',
  templateLanguage: '',
});

const templateStatus = ref(null);
const templateLoading = ref(false);
const confirmDialog = ref(null);
const templateSelectorRef = ref(null);
const templateMode = ref('create_new');
const selectedExistingTemplateName = ref('');
const bodyVariables = ref({});
const existingTemplateBody = ref('');
const existingTemplateButtonText = ref('');

const templateModeTabs = computed(() => [
  { label: t('INBOX_MGMT.CSAT.TEMPLATE_MODE.CREATE_NEW'), key: 0 },
  { label: t('INBOX_MGMT.CSAT.TEMPLATE_MODE.USE_EXISTING'), key: 1 },
]);

const activeTemplateModeTabIndex = computed(() =>
  templateMode.value === 'use_existing' ? 1 : 0
);

const onTemplateModeTabChange = tab => {
  templateMode.value = tab.key === 1 ? 'use_existing' : 'create_new';
};

const originalTemplateValues = ref({
  message: '',
  templateButtonText: '',
  templateLanguage: '',
});

const filterTypes = [
  {
    label: t('INBOX_MGMT.CSAT.SURVEY_RULE.OPERATOR.CONTAINS'),
    value: 'contains',
  },
  {
    label: t('INBOX_MGMT.CSAT.SURVEY_RULE.OPERATOR.DOES_NOT_CONTAINS'),
    value: 'does_not_contain',
  },
];

const labelOptions = computed(() =>
  labels.value?.length
    ? labels.value
        .map(label => ({ label: label.title, value: label.title }))
        .filter(label => !selectedLabelValues.value.includes(label.value))
    : []
);

const languageOptions = computed(() =>
  whatsappTemplateLanguages.map(({ name, id }) => ({
    label: `${name} (${id})`,
    value: id,
  }))
);

const resolvedExistingTemplateBody = computed(() => {
  if (!existingTemplateBody.value) return '';
  let message = existingTemplateBody.value;
  Object.entries(bodyVariables.value).forEach(([key, value]) => {
    if (value) {
      message = message.replaceAll(`{{${key}}}`, value);
    }
  });
  return message;
});

const messagePreviewData = computed(() => {
  if (templateMode.value === 'use_existing') {
    return {
      content:
        resolvedExistingTemplateBody.value ||
        t('INBOX_MGMT.CSAT.MESSAGE.PLACEHOLDER'),
    };
  }
  return {
    content: state.message || t('INBOX_MGMT.CSAT.MESSAGE.PLACEHOLDER'),
  };
});

const previewButtonText = computed(() => {
  if (templateMode.value === 'use_existing') {
    return (
      existingTemplateButtonText.value ||
      t('INBOX_MGMT.CSAT.BUTTON_TEXT.PLACEHOLDER')
    );
  }
  return state.templateButtonText;
});

const shouldShowTemplateStatus = computed(
  () =>
    templateStatus.value &&
    !templateLoading.value &&
    templateMode.value !== 'use_existing'
);

const isUpdateDisabled = computed(() => {
  if (
    templateMode.value === 'use_existing' &&
    isAWhatsAppCloudChannel.value &&
    state.csatSurveyEnabled &&
    !selectedExistingTemplateName.value
  ) {
    return true;
  }
  return false;
});

const templateApprovalStatus = computed(() => {
  const statusMap = {
    APPROVED: {
      text: t('INBOX_MGMT.CSAT.TEMPLATE_STATUS.APPROVED'),
      icon: 'i-lucide-circle-check',
      color: 'text-n-teal-11',
    },
    PENDING: {
      text: t('INBOX_MGMT.CSAT.TEMPLATE_STATUS.PENDING'),
      icon: 'i-lucide-clock',
      color: 'text-n-amber-11',
    },
    REJECTED: {
      text: t('INBOX_MGMT.CSAT.TEMPLATE_STATUS.REJECTED'),
      icon: 'i-lucide-circle-x',
      color: 'text-n-ruby-10',
    },
  };

  // Handle template not found case
  if (templateStatus.value?.error === 'TEMPLATE_NOT_FOUND') {
    return {
      text: t('INBOX_MGMT.CSAT.TEMPLATE_STATUS.NOT_FOUND'),
      icon: 'i-lucide-alert-triangle',
      color: 'text-n-ruby-10',
    };
  }

  // Handle existing template with status
  if (templateStatus.value?.template_exists && templateStatus.value.status) {
    // Convert status to uppercase for consistency with statusMap keys
    const normalizedStatus = templateStatus.value.status.toUpperCase();
    return statusMap[normalizedStatus] || statusMap.PENDING;
  }

  // Default case - no template exists
  return {
    text: t('INBOX_MGMT.CSAT.TEMPLATE_STATUS.DEFAULT'),
    icon: 'i-lucide-stamp',
    color: 'text-n-slate-11',
  };
});

const initializeState = () => {
  if (!props.inbox) return;

  const { csat_survey_enabled, csat_config } = props.inbox;

  state.csatSurveyEnabled = csat_survey_enabled || false;

  if (!csat_config) return;

  const {
    display_type: displayType = CSAT_DISPLAY_TYPES.EMOJI,
    message = '',
    button_text: buttonText = 'Please rate us',
    language = 'en',
    survey_rules: surveyRules = {},
  } = csat_config;

  state.displayType = displayType;
  state.message = message;
  state.templateButtonText = buttonText;
  state.templateLanguage = language;
  state.surveyRuleOperator = surveyRules.operator || 'contains';

  selectedLabelValues.value = Array.isArray(surveyRules.values)
    ? [...surveyRules.values]
    : [];

  // Store original template values for change detection
  if (isTemplateRequiredWhatsAppChannel.value) {
    originalTemplateValues.value = {
      message: state.message,
      templateButtonText: state.templateButtonText,
      templateLanguage: state.templateLanguage,
    };

    // Set template mode based on stored source
    const templateSource = csat_config?.template?.source;
    if (templateSource === 'user_selected') {
      templateMode.value = 'use_existing';
      selectedExistingTemplateName.value = csat_config.template.name || '';
      bodyVariables.value = csat_config.template.body_variables || {};
      existingTemplateBody.value = message;
      existingTemplateButtonText.value = buttonText;
      // Reset create-new fields so they don't show stale data
      state.message = '';
      state.templateButtonText = 'Please rate us';
    } else {
      templateMode.value = 'create_new';
    }
  }
};

const checkTemplateStatus = async () => {
  if (!isTemplateRequiredWhatsAppChannel.value) return;

  try {
    templateLoading.value = true;
    const response = await store.dispatch('inboxes/getCSATTemplateStatus', {
      inboxId: props.inbox.id,
    });

    // Handle case where template doesn't exist
    if (!response.template_exists && response.error === 'Template not found') {
      templateStatus.value = {
        template_exists: false,
        error: 'TEMPLATE_NOT_FOUND',
      };
    } else {
      templateStatus.value = response;
    }
  } catch (error) {
    templateStatus.value = {
      template_exists: false,
      error: 'API_ERROR',
    };
  } finally {
    templateLoading.value = false;
  }
};

onMounted(() => {
  initializeState();
  if (!labels.value?.length) store.dispatch('labels/get');
  if (isTemplateRequiredWhatsAppChannel.value) checkTemplateStatus();
});

watch(() => props.inbox, initializeState, { immediate: true });

const handleLabelSelect = value => {
  if (!value || selectedLabelValues.value.includes(value)) {
    return;
  }

  selectedLabelValues.value.push(value);
};

const updateDisplayType = type => {
  state.displayType = type;
};

const updateSurveyRuleOperator = operator => {
  state.surveyRuleOperator = operator;
};

const removeLabel = label => {
  const index = selectedLabelValues.value.indexOf(label);
  if (index !== -1) {
    selectedLabelValues.value.splice(index, 1);
  }
};

// Check if template-related fields have changed
const hasTemplateChanges = () => {
  if (!isTemplateRequiredWhatsAppChannel.value) return false;

  const original = originalTemplateValues.value;
  return (
    original.message !== state.message ||
    original.templateButtonText !== state.templateButtonText ||
    original.templateLanguage !== state.templateLanguage
  );
};

// Check if there's an existing template
const hasExistingTemplate = () => {
  const { template_exists, error } = templateStatus.value || {};
  return template_exists && !error;
};

// Check if we should create a template
const shouldCreateTemplate = () => {
  // Create template if no existing template
  if (!hasExistingTemplate()) {
    return true;
  }

  // Create template if there are changes to template fields
  return hasTemplateChanges();
};

// Build template config for saving
const buildTemplateConfig = () => {
  if (!hasExistingTemplate()) {
    return null;
  }

  const { template_name, template_id, template, status } =
    templateStatus.value || {};

  if (isATwilioWhatsAppChannel.value) {
    // Twilio WhatsApp format - get from existing template config
    const existingTemplate = props.inbox?.csat_config?.template;

    return existingTemplate
      ? {
          friendly_name: existingTemplate.friendly_name,
          content_sid: existingTemplate.content_sid,
          language: existingTemplate.language || state.templateLanguage,
          status: existingTemplate.status || status,
        }
      : null;
  }

  // WhatsApp Cloud format
  return {
    name: template_name,
    template_id,
    language: template?.language || state.templateLanguage,
    status,
  };
};

const updateInbox = async attributes => {
  const payload = {
    id: props.inbox.id,
    formData: false,
    ...attributes,
  };

  await store.dispatch('inboxes/updateInbox', payload);
};

const createTemplate = async () => {
  if (!isTemplateRequiredWhatsAppChannel.value) return null;

  const response = await store.dispatch('inboxes/createCSATTemplate', {
    inboxId: props.inbox.id,
    template: {
      message: state.message,
      button_text: state.templateButtonText,
      language: state.templateLanguage,
    },
  });
  useAlert(t('INBOX_MGMT.CSAT.TEMPLATE_CREATION.SUCCESS_MESSAGE'));
  return response.template;
};

const linkTemplate = async () => {
  if (!selectedExistingTemplateName.value) return null;

  const response = await store.dispatch('inboxes/linkCSATTemplate', {
    inboxId: props.inbox.id,
    template: {
      name: selectedExistingTemplateName.value,
      language: state.templateLanguage,
      body_variables: bodyVariables.value,
    },
  });
  useAlert(t('INBOX_MGMT.CSAT.EXISTING_TEMPLATE.LINK_SUCCESS'));
  return response.template;
};

const handleTemplateSelected = template => {
  state.templateLanguage = template.language || 'en';
  existingTemplateBody.value = template.body_text || '';
  existingTemplateButtonText.value =
    template.button_text || t('INBOX_MGMT.CSAT.BUTTON_TEXT.PLACEHOLDER');
};

const performSave = async () => {
  try {
    isUpdating.value = true;
    let newTemplateData = null;

    // For WhatsApp channels, handle template based on mode
    if (isTemplateRequiredWhatsAppChannel.value && state.csatSurveyEnabled) {
      if (
        templateMode.value === 'use_existing' &&
        isAWhatsAppCloudChannel.value
      ) {
        // Link existing template mode — require selection
        if (!selectedExistingTemplateName.value) return;

        // Validate all body variables are filled
        if (
          templateSelectorRef.value &&
          !templateSelectorRef.value.validate()
        ) {
          useAlert(t('INBOX_MGMT.CSAT.TEMPLATE_VARIABLES.VALIDATION_ERROR'));
          return;
        }

        try {
          newTemplateData = await linkTemplate();
        } catch (error) {
          const errorMessage =
            error.response?.data?.error ||
            t('INBOX_MGMT.CSAT.EXISTING_TEMPLATE.LINK_ERROR');
          useAlert(errorMessage);
          return;
        }
      } else if (shouldCreateTemplate()) {
        // Create new template mode
        try {
          newTemplateData = await createTemplate();
        } catch (error) {
          const errorMessage =
            error.response?.data?.error ||
            t('INBOX_MGMT.CSAT.TEMPLATE_CREATION.ERROR_MESSAGE');
          useAlert(errorMessage);
          return;
        }
      }
    }

    const csatConfig = {
      display_type: state.displayType,
      message:
        templateMode.value === 'use_existing'
          ? existingTemplateBody.value
          : state.message,
      button_text:
        templateMode.value === 'use_existing'
          ? existingTemplateButtonText.value
          : state.templateButtonText,
      language: state.templateLanguage,
      survey_rules: {
        operator: state.surveyRuleOperator,
        values: selectedLabelValues.value,
      },
    };

    // Use new template data if created/linked, otherwise preserve existing template information
    if (newTemplateData) {
      if (newTemplateData.source === 'user_selected') {
        // User-selected existing template
        csatConfig.template = {
          name: newTemplateData.name,
          template_id: newTemplateData.template_id,
          language: newTemplateData.language,
          status: newTemplateData.status,
          source: 'user_selected',
          linked_at: newTemplateData.linked_at,
          body_variables: bodyVariables.value,
        };
      } else if (isATwilioWhatsAppChannel.value) {
        // Twilio WhatsApp template format
        csatConfig.template = {
          friendly_name: newTemplateData.friendly_name,
          content_sid: newTemplateData.content_sid,
          language: newTemplateData.language,
          status: newTemplateData.status,
          source: 'auto_created',
          created_at: new Date().toISOString(),
        };
      } else {
        // WhatsApp Cloud template format
        csatConfig.template = {
          name: newTemplateData.name,
          template_id: newTemplateData.template_id,
          language: newTemplateData.language,
          status: newTemplateData.status,
          source: 'auto_created',
          created_at: new Date().toISOString(),
        };
      }
    } else {
      const templateConfig = buildTemplateConfig();
      if (templateConfig) {
        csatConfig.template = templateConfig;
      }
    }

    await updateInbox({
      csat_survey_enabled: state.csatSurveyEnabled,
      csat_config: csatConfig,
    });

    useAlert(t('INBOX_MGMT.CSAT.API.SUCCESS_MESSAGE'));
    checkTemplateStatus();
  } catch (error) {
    useAlert(t('INBOX_MGMT.CSAT.API.ERROR_MESSAGE'));
  } finally {
    isUpdating.value = false;
  }
};

const saveSettings = async () => {
  // For "use existing" mode, no confirmation needed — just save
  if (templateMode.value === 'use_existing') {
    await performSave();
    return;
  }

  // Check if we need to show confirmation dialog for WhatsApp template changes
  // This applies to both WhatsApp Cloud and Twilio WhatsApp channels
  if (
    isTemplateRequiredWhatsAppChannel.value &&
    state.csatSurveyEnabled &&
    hasExistingTemplate() &&
    hasTemplateChanges()
  ) {
    // Only show dialog if the existing template was auto-created (will be deleted)
    const existingSource = props.inbox?.csat_config?.template?.source;
    if (existingSource !== 'user_selected') {
      confirmDialog.value?.open();
      return;
    }
  }

  await performSave();
};

const handleConfirmTemplateUpdate = async () => {
  // We will delete the template before creating the template
  await performSave();
};
</script>

<template>
  <div class="mx-8">
    <SectionLayout
      :title="$t('INBOX_MGMT.CSAT.TITLE')"
      :description="$t('INBOX_MGMT.CSAT.SUBTITLE')"
    >
      <template #headerActions>
        <div class="flex justify-end">
          <Switch v-model="state.csatSurveyEnabled" />
        </div>
      </template>

      <div class="grid gap-5">
        <!-- Show display type only for non-WhatsApp channels -->
        <WithLabel
          v-if="!isTemplateRequiredWhatsAppChannel"
          :label="$t('INBOX_MGMT.CSAT.DISPLAY_TYPE.LABEL')"
          name="display_type"
        >
          <CSATDisplayTypeSelector
            :selected-type="state.displayType"
            @update="updateDisplayType"
          />
        </WithLabel>

        <template v-if="isTemplateRequiredWhatsAppChannel">
          <!-- Template source toggle (only for WhatsApp Cloud) -->
          <WithLabel
            v-if="isAWhatsAppCloudChannel"
            :label="$t('INBOX_MGMT.CSAT.TEMPLATE_MODE.LABEL')"
            name="template_mode"
          >
            <TabBar
              :tabs="templateModeTabs"
              :initial-active-tab="activeTemplateModeTabIndex"
              @tab-changed="onTemplateModeTabChange"
            />
          </WithLabel>

          <div
            class="flex flex-col gap-4 justify-between w-full lg:flex-row lg:gap-6"
          >
            <div class="flex flex-col gap-3 basis-3/5">
              <!-- Use existing template mode -->
              <template
                v-if="
                  templateMode === 'use_existing' && isAWhatsAppCloudChannel
                "
              >
                <ExistingTemplateSelector
                  ref="templateSelectorRef"
                  v-model="selectedExistingTemplateName"
                  :inbox-id="inbox.id"
                  :body-variables="bodyVariables"
                  @template-selected="handleTemplateSelected"
                  @update:body-variables="bodyVariables = $event"
                />
              </template>

              <!-- Create new template mode -->
              <template v-else>
                <WithLabel
                  :label="$t('INBOX_MGMT.CSAT.MESSAGE.LABEL')"
                  name="message"
                >
                  <Editor
                    v-model="state.message"
                    :placeholder="$t('INBOX_MGMT.CSAT.MESSAGE.PLACEHOLDER')"
                    :max-length="200"
                    channel-type="Context::Plain"
                    class="w-full"
                  />
                </WithLabel>
                <Input
                  v-model="state.templateButtonText"
                  :label="$t('INBOX_MGMT.CSAT.BUTTON_TEXT.LABEL')"
                  :placeholder="$t('INBOX_MGMT.CSAT.BUTTON_TEXT.PLACEHOLDER')"
                  class="w-full"
                />

                <WithLabel
                  v-if="shouldShowTemplateStatus"
                  :label="$t('INBOX_MGMT.CSAT.LANGUAGE.LABEL')"
                  name="language"
                >
                  <ComboBox
                    v-model="state.templateLanguage"
                    :options="languageOptions"
                    :placeholder="$t('INBOX_MGMT.CSAT.LANGUAGE.PLACEHOLDER')"
                  />
                </WithLabel>
              </template>

              <div
                v-if="shouldShowTemplateStatus"
                class="flex gap-2 items-center mt-4"
              >
                <Icon
                  :icon="templateApprovalStatus.icon"
                  :class="templateApprovalStatus.color"
                  class="size-4"
                />
                <span
                  :class="templateApprovalStatus.color"
                  class="text-sm font-medium"
                >
                  {{ templateApprovalStatus.text }}
                </span>
              </div>
            </div>

            <div
              class="flex flex-col flex-shrink-0 justify-start items-center p-6 mt-1 rounded-xl basis-2/5 bg-n-slate-2 outline outline-1 outline-n-weak"
            >
              <p
                class="inline-flex items-center text-sm font-medium text-n-slate-11"
              >
                {{ $t('INBOX_MGMT.CSAT.MESSAGE_PREVIEW.LABEL') }}
                <Icon
                  v-tooltip.top-end="
                    $t('INBOX_MGMT.CSAT.MESSAGE_PREVIEW.TOOLTIP')
                  "
                  icon="i-lucide-info"
                  class="flex-shrink-0 mx-1 size-4"
                />
              </p>
              <CSATTemplate
                :message="messagePreviewData"
                :button-text="previewButtonText"
                class="pt-12"
              />
            </div>
          </div>
        </template>

        <!-- Non-WhatsApp channels layout -->
        <template v-else>
          <WithLabel
            :label="$t('INBOX_MGMT.CSAT.MESSAGE.LABEL')"
            name="message"
          >
            <Editor
              v-model="state.message"
              :placeholder="$t('INBOX_MGMT.CSAT.MESSAGE.PLACEHOLDER')"
              :max-length="200"
              class="w-full"
            />
          </WithLabel>
        </template>

        <WithLabel
          :label="$t('INBOX_MGMT.CSAT.SURVEY_RULE.LABEL')"
          name="survey_rule"
        >
          <div class="mb-4">
            <span
              class="inline-flex flex-wrap gap-1.5 items-center text-sm text-n-slate-12"
            >
              {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.DESCRIPTION_PREFIX') }}
              <FilterSelect
                v-model="state.surveyRuleOperator"
                variant="faded"
                :options="filterTypes"
                class="inline-flex shrink-0"
                @update:model-value="updateSurveyRuleOperator"
              />
              {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.DESCRIPTION_SUFFIX') }}

              <NextButton
                v-for="label in selectedLabelValues"
                :key="label"
                sm
                faded
                slate
                trailing-icon
                :label="label"
                icon="i-lucide-x"
                class="inline-flex shrink-0"
                @click="removeLabel(label)"
              />
              <FilterSelect
                v-model="currentLabel"
                :options="labelOptions"
                :label="$t('INBOX_MGMT.CSAT.SURVEY_RULE.SELECT_PLACEHOLDER')"
                hide-label
                variant="faded"
                class="inline-flex shrink-0"
                @update:model-value="handleLabelSelect"
              />
            </span>
          </div>
        </WithLabel>
        <p class="text-sm italic text-n-slate-11">
          {{
            isTemplateRequiredWhatsAppChannel
              ? $t('INBOX_MGMT.CSAT.WHATSAPP_NOTE')
              : $t('INBOX_MGMT.CSAT.NOTE')
          }}
        </p>
        <div>
          <NextButton
            type="submit"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="isUpdating"
            :disabled="isUpdateDisabled"
            @click="saveSettings"
          />
        </div>
      </div>
    </SectionLayout>

    <!-- Template Update Confirmation Dialog -->
    <ConfirmTemplateUpdateDialog
      ref="confirmDialog"
      @confirm="handleConfirmTemplateUpdate"
    />
  </div>
</template>
